/**
 * The Order together look, in one place: the site's own palette and type
 * (components/LusukuPromise.jsx), phone first, 44px touch targets, white
 * cards with a soft shadow on the light green surface.
 */
import styled, { css } from "styled-components";

export const INK = "#2f474e";
export const INK_DEEP = "#24525f";
export const GREEN = "#3D6A0B";
export const GREEN_SOFT = "#eef5e4";
export const LIME = "#b9d98f";
// Invitation accents; payment success and warning colours keep their meanings.
export const SOCIAL = "#76365E";
export const SOCIAL_SOFT = "#FFF0F5";
export const SOCIAL_LINE = "#E6BED2";
export const SURFACE = "#f3f7ef";
export const TEXT = "#15232a";
export const MUTED = "#55646a";
export const LINE = "#edf0ea";
export const AMBER = "#8a5a00";
export const AMBER_SOFT = "#fff4dc";
export const DANGER = "#a43118";
export const DANGER_SOFT = "#fbebe7";

export const FONT = '"Urbanist", "Inter", "Segoe UI", sans-serif';

export const Page = styled.div`
  min-height: 100vh;
  background: ${SURFACE};
  font-family: ${FONT};
  color: ${TEXT};
  padding-top: var(--lusuku-nav-h, 130px);
  padding-bottom: 48px;
`;

export const Hero = styled.header`
  background: ${INK};
  color: #ffffff;
  padding: 20px 16px 56px;

  @media (min-width: 900px) {
    padding: 28px 32px 64px;
  }
`;

export const HeroInner = styled.div`
  max-width: ${({ $wide }) => $wide ? "1088px" : "720px"};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const Shell = styled.main`
  max-width: ${({ $wide }) => $wide ? "1120px" : "720px"};
  margin: -36px auto 0;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Card = styled.section`
  background: #ffffff;
  border-radius: 18px;
  padding: 18px 16px;
  box-shadow: 0 12px 30px rgba(21, 35, 42, 0.08);

  @media (min-width: 600px) {
    padding: 22px 22px;
  }
`;

export const Eyebrow = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(p) => (p.$onDark ? LIME : GREEN)};
`;

export const HeroTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-wrap: balance;

  @media (min-width: 900px) {
    font-size: 36px;
  }
`;

export const HeroLine = styled.p`
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 15px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.84);

  svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: ${LIME};
    font-size: 18px;
  }
`;

export const CardTitle = styled.h2`
  margin: 0 0 4px;
  font-size: 19px;
  line-height: 1.25;
  font-weight: 800;
  color: ${TEXT};
`;

export const Muted = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${MUTED};
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || 10}px;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 20px;
  border-radius: 12px;
  border: 1.5px solid transparent;
  font-family: ${FONT};
  font-size: 15px;
  font-weight: 800;
  line-height: 1.2;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease;
  flex: 1 1 160px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 3px solid ${LIME};
    outline-offset: 2px;
  }

  svg {
    font-size: 20px;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  background: ${GREEN};
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #335a09;
  }
`;

export const InkButton = styled.button`
  ${buttonBase}
  background: ${INK};
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #243a40;
  }
`;

export const QuietButton = styled.button`
  ${buttonBase}
  background: #ffffff;
  color: ${INK};
  border-color: #d5ddd0;

  &:hover:not(:disabled) {
    border-color: ${INK};
  }
`;

export const DangerButton = styled.button`
  ${buttonBase}
  background: ${DANGER};
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #8c2913;
  }
`;

/* Small text actions inside a row: still a 44px target. */
export const TextButton = styled.button`
  min-height: 44px;
  min-width: 44px;
  padding: 0 8px;
  border: none;
  background: none;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 800;
  color: ${(p) => (p.$danger ? DANGER : INK_DEEP)};
  cursor: pointer;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    color: #9aa6a9;
    cursor: not-allowed;
  }
`;

const TONES = {
  done: { bg: GREEN_SOFT, fg: GREEN },
  wait: { bg: AMBER_SOFT, fg: AMBER },
  stopped: { bg: "#eef0f1", fg: MUTED },
  todo: { bg: "#ffffff", fg: INK, border: "#d5ddd0" },
  danger: { bg: DANGER_SOFT, fg: DANGER },
  success: { bg: GREEN_SOFT, fg: GREEN },
  pending: { bg: AMBER_SOFT, fg: AMBER },
  dark: { bg: "rgba(255, 255, 255, 0.14)", fg: "#ffffff" },
};

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 800;
  white-space: nowrap;
  background: ${(p) => (TONES[p.$tone] || TONES.todo).bg};
  color: ${(p) => (TONES[p.$tone] || TONES.todo).fg};
  border: 1px solid ${(p) => (TONES[p.$tone] || TONES.todo).border || "transparent"};

  svg {
    font-size: 15px;
  }
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${LINE};
  margin: 14px 0;
`;

export const MoneyRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  font-size: 15px;
  color: ${(p) => (p.$strong ? TEXT : MUTED)};
  font-weight: ${(p) => (p.$strong ? 800 : 600)};

  strong,
  b {
    color: ${TEXT};
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }
`;

export const Notice = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.45;
  background: ${(p) => (p.$tone === "danger" ? DANGER_SOFT : p.$tone === "wait" ? AMBER_SOFT : GREEN_SOFT)};
  color: ${(p) => (p.$tone === "danger" ? DANGER : p.$tone === "wait" ? AMBER : TEXT)};

  svg {
    flex-shrink: 0;
    font-size: 20px;
  }
`;

export const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 800;
  color: ${TEXT};
  margin: 0 0 6px;
`;

export const TextInput = styled.input`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  min-height: 48px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1.5px solid #d5ddd0;
  background: #ffffff;
  font-family: ${FONT};
  font-size: 16px;
  color: ${TEXT};
  outline: none;

  &:focus {
    border-color: ${GREEN};
  }
`;

export const SectionLabel = styled.h3`
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${MUTED};
`;

/* Choice cards (payment mode, cut-off, payment method). */
export const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$columns ? `repeat(${p.$columns}, minmax(0, 1fr))` : "1fr")};
  gap: 10px;
`;

export const Choice = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1.5px solid ${(p) => (p.$selected ? GREEN : "#d5ddd0")};
  background: ${(p) => (p.$selected ? GREEN_SOFT : "#ffffff")};
  font-family: ${FONT};
  text-align: left;
  color: ${TEXT};
  cursor: pointer;
  transition: border-color 150ms ease, background-color 150ms ease;

  &:hover:not(:disabled) {
    border-color: ${GREEN};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 3px solid ${LIME};
    outline-offset: 2px;
  }

  strong {
    font-size: 15px;
    font-weight: 800;
  }

  span {
    font-size: 13px;
    line-height: 1.4;
    color: ${MUTED};
  }
`;

export const ChipChoice = styled(Choice)`
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border-radius: 999px;

  strong {
    font-size: 14px;
  }
`;

/* A full-width sheet title row inside an MUI Dialog. */
export const SheetHead = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 16px 8px;
  font-family: ${FONT};

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    color: ${TEXT};
  }

  p {
    margin: 4px 0 0;
    font-size: 14px;
    color: ${MUTED};
  }
`;

export const SheetBody = styled.div`
  padding: 8px 16px 20px;
  font-family: ${FONT};
  color: ${TEXT};
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const SheetFoot = styled.div`
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  padding: 12px 16px 16px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  background: #ffffff;
  border-top: 1px solid ${LINE};
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-family: ${FONT};
`;

export const IconCircle = styled.button`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: ${SURFACE};
  color: ${INK};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: ${GREEN_SOFT};
  }
`;

/* Paper props for the sheets on desktop (phones get them full screen). The
   paper keeps its own scrolling, so the sticky footer stays in view. */
export const sheetPaperProps = {
  style: { borderRadius: 20 },
};
